import React, { useState, useEffect, useCallback } from 'react';
import './EnhancedTimelineModal.css';
import { getCandidateTimeline } from '../services/apiService';


const EnhancedTimelineModal = ({ isOpen, onClose, application }) => {
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    // Enhanced status mapping (matching Flutter logic)
    const mapStatusStyle = useCallback((raw) => {
        const s = String(raw || '').toLowerCase();
        if (s.includes('applied') || s === 'submitted' || s === 'assigned') return { label: 'Applied', icon: 'fa-paper-plane', color: '#3B82F6' };
        if (s.includes('under') || s.includes('review')) return null; // hide Under Review
        if (s.includes('short') || s === 'approved') return { label: 'Shortlisted', icon: 'fa-check-circle', color: '#10B981' };
        if (s.includes('interview') && (s.includes('schedule') || s === 'interview scheduled')) return { label: 'Interview Scheduled', icon: 'fa-calendar-check', color: '#8B5CF6' };
        if (s === 'interview' || s.includes('interview done') || s.includes('interview round')) return { label: 'Interview', icon: 'fa-user-tie', color: '#8B5CF6' };
        if (s.includes('select') || s.includes('offer')) return { label: 'Selected', icon: 'fa-star', color: '#EC4899' };
        if (s.includes('hired') || s.includes('joined')) return { label: 'Hired', icon: 'fa-user-check', color: '#10B981' };
        if (s.includes('reject') || s.includes('not selected') || s.includes('removed')) return { label: 'Rejected', icon: 'fa-times-circle', color: '#EF4444' };
        if (s.includes('no show') || s.includes('no-show')) return { label: 'No Show', icon: 'fa-user-times', color: '#F97316' };
        if (s.includes('withdraw') || s.includes('candidate withdrew')) return { label: 'Withdrawn', icon: 'fa-undo', color: '#F97316' };
        if (s.includes('cancelled') || s.includes('position closed')) return { label: 'Cancelled', icon: 'fa-ban', color: '#F97316' };
        if (s.includes('in process')) return { label: 'In Process', icon: 'fa-hourglass-half', color: '#F59E0B' };
        return { label: raw || 'Status', icon: 'fa-info-circle', color: '#64748b' };
    }, []);

    const defaultDescriptionFor = useCallback((label, app) => {
        switch (label) {
            case 'Applied':
                return `Successfully submitted application for ${app?.jobTitle || 'position'} at ${app?.company || 'company'}`;
            case 'Shortlisted':
                return 'Your profile has been shortlisted for further evaluation';
            case 'Interview Scheduled':
                return 'Interview has been scheduled with the hiring team';
            case 'Interview':
                return 'Interview in progress or completed';
            case 'Selected':
                return 'Congratulations! You have been selected for this position';
            case 'Hired':
                return 'Welcome to the team! Your onboarding process will begin soon';
            case 'Not Selected':
                return 'Unfortunately, you were not selected for this position. Keep applying!';
            case 'No Show':
                return 'You missed the scheduled interview';
            case 'Withdrawn':
                return 'Application withdrawn by candidate';
            default:
                return '';
        }
    }, []);

    // Validate a date-like input; reject null/undefined, zero-dates, and invalid Date
    const isValidTimelineDate = (val) => {
        if (!val) return false;
        const s = String(val).trim();
        if (!s) return false;
        // Common zero-date patterns from APIs
        if (/^0{4}[:-]0{2}[:-]0{2}/.test(s)) return false; // 0000:00:00 00:00:00 or 0000-00-00
        const d = new Date(s);
        return !isNaN(d.getTime());
    };

    const createTimelineFromEvents = useCallback((app) => {
        const events = (app && (app.timeline || app.applicationTimeline || app.events)) || [];
        if (!Array.isArray(events) || events.length === 0) return [];

        const normalized = events
            .map((e, idx) => {
                const statusRaw = e.status || e.candidateStatus || e.event || e.stage || '';
                const msg = e.description || e.message || e.note || '';

                let dt = null;
                const s = String(statusRaw || '').toLowerCase();

                if (s.includes('select')) {
                    dt = e.processedOn || e.createdOn || e.createdAt || e.date || e.timestamp || e.time || null;
                } else if (s.includes('schedule')) {
                    dt = e.actualDate || e.createdOn || e.createdAt || e.date || e.timestamp || e.time || null;
                } else {
                    dt = e.createdOn || e.createdAt || e.date || e.timestamp || e.time || null;
                }

                // Only include events that have a valid date; skip null/invalid/zero-date values
                if (!isValidTimelineDate(dt)) return null;
                const when = new Date(dt);
                const meta = mapStatusStyle(statusRaw);
                if (!meta) return null; // skip statuses we don't want to show
                return {
                    status: meta.label,
                    date: when,
                    isCompleted: false,
                    isActive: false,
                    description: msg || defaultDescriptionFor(meta.label, app),
                    icon: meta.icon,
                    color: meta.color,
                    _order: idx,
                    rawStatus: s, // Keep raw status for sorting
                };
            })
            .filter(Boolean)
            .sort((a, b) => {
                const aIsSelected = a.rawStatus.includes('select');
                const bIsSelected = b.rawStatus.includes('select');
                const aIsInterview = a.rawStatus.includes('interview');
                const bIsInterview = b.rawStatus.includes('interview');

                // If 'a' is 'Selected' and 'b' is 'Interview', 'a' should come after 'b'
                if (aIsSelected && bIsInterview) return 1;
                // If 'b' is 'Selected' and 'a' is 'Interview', 'a' should come before 'b'
                if (bIsSelected && aIsInterview) return -1;

                // Fallback to date sorting
                if (a.date && b.date) return a.date - b.date;
                if (a.date && !b.date) return -1;
                if (!a.date && b.date) return 1;
                return a._order - b._order;
            });

        let lastDatedIndex = -1;
        normalized.forEach((n, i) => { if (n.date) lastDatedIndex = i; });
        normalized.forEach((n, i) => {
            n.isCompleted = i <= lastDatedIndex;
            n.isActive = i === (lastDatedIndex + 1) && !n.date;
        });

        const hasApplied = normalized.some(n => n.status === 'Applied');
        if (!hasApplied) {
            const appliedDateRaw = app.appliedOn || app.appliedDate || null;
            if (isValidTimelineDate(appliedDateRaw)) {
                const appliedDate = new Date(appliedDateRaw);
                normalized.unshift({
                    status: 'Applied',
                    date: appliedDate,
                    isCompleted: true,
                    isActive: false,
                    description: defaultDescriptionFor('Applied', app),
                    icon: 'fa-paper-plane',
                    color: '#3B82F6',
                    _order: -1,
                });
            }
        }

        return normalized;
    }, [mapStatusStyle, defaultDescriptionFor]);

    const createTimelineFromApplication = useCallback((app) => {
        const timeline = [];
        const appliedDate = new Date(app.appliedDate);
        const statusLower = (app.status || '').toLowerCase();

        // Always start with Applied
        timeline.push({
            status: 'Applied',
            date: appliedDate,
            isCompleted: true,
            isActive: false,
            description: `Successfully submitted application for ${app.jobTitle || 'position'} at ${app.company || 'company'}`,
            icon: 'fa-check-circle',
            color: '#10B981'
        });

        if (statusLower.includes('shortlist') || statusLower.includes('approved') || 
            statusLower.includes('interview') || statusLower.includes('selected') || 
            statusLower.includes('hired')) {
            const shortlistDate = new Date(appliedDate.getTime() + (2 * 24 * 60 * 60 * 1000));
            timeline.push({
                status: 'Shortlisted',
                date: shortlistDate,
                isCompleted: true,
                isActive: false,
                description: 'Your profile has been shortlisted for further evaluation',
                icon: 'fa-check-circle',
                color: '#10B981'
            });
        }

        if (statusLower.includes('interview') || statusLower.includes('selected') || statusLower.includes('hired')) {
            const interviewDate = new Date(appliedDate.getTime() + (5 * 24 * 60 * 60 * 1000));
            timeline.push({
                status: 'Interview Scheduled',
                date: interviewDate,
                isCompleted: statusLower.includes('selected') || statusLower.includes('hired'),
                isActive: statusLower.includes('interview') && !statusLower.includes('selected'),
                description: 'Interview has been scheduled with the hiring team',
                icon: 'fa-calendar-check',
                color: '#8B5CF6'
            });
        }

        if (statusLower.includes('selected') || statusLower.includes('hired')) {
            const selectedDate = new Date(appliedDate.getTime() + (8 * 24 * 60 * 60 * 1000));
            timeline.push({
                status: 'Selected',
                date: selectedDate,
                isCompleted: statusLower.includes('hired'),
                isActive: statusLower.includes('selected') && !statusLower.includes('hired'),
                description: 'Congratulations! You have been selected for this position',
                icon: 'fa-star',
                color: '#EC4899'
            });
        }

        if (statusLower.includes('hired') || statusLower.includes('joined')) {
            const hiredDate = new Date(appliedDate.getTime() + (10 * 24 * 60 * 60 * 1000));
            timeline.push({
                status: 'Hired',
                date: hiredDate,
                isCompleted: true,
                isActive: false,
                description: 'Welcome to the team! Your onboarding process will begin soon',
                icon: 'fa-user-check',
                color: '#10B981'
            });
        }

        if (statusLower.includes('reject')) {
            const rejectedDate = new Date(appliedDate.getTime() + (3 * 24 * 60 * 60 * 1000));
            timeline.push({
                status: 'Rejected',
                date: rejectedDate,
                isCompleted: true,
                isActive: false,
                description: 'Unfortunately, you were not selected for this position. Keep applying!',
                icon: 'fa-times-circle',
                color: '#EF4444'
            });
        }

        if (!statusLower.includes('reject') && !statusLower.includes('hired')) {
            if (statusLower.includes('shortlist')) {
                if (!statusLower.includes('interview')) {
                    timeline.push({
                        status: 'Interview Pending',
                        date: null,
                        isCompleted: false,
                        isActive: true,
                        description: 'Waiting for interview to be scheduled',
                        icon: 'fa-clock',
                        color: '#F59E0B'
                    });
                } else if (!statusLower.includes('selected')) {
                    timeline.push({
                        status: 'Final Decision',
                        date: null,
                        isCompleted: false,
                        isActive: true,
                        description: 'Final decision is pending',
                        icon: 'fa-hourglass-half',
                        color: '#F59E0B'
                    });
                }
            }
        }

        return timeline;
    }, []);

    const resolveOpeningCandidateDetailId = useCallback((app) => {
        if (!app) return null;
        // Try common keys
        return (
            app.openingCandidateDetailId ||
            app.openingCandidateId ||
            app.applicationId ||
            app.id ||
            null
        );
    }, []);

    const fetchAndGenerateTimeline = useCallback(async () => {
        if (!application) return;
        setLoading(true);
        setError(null);
        try {
            const id = resolveOpeningCandidateDetailId(application);
            let apiEvents = [];
            if (id) {
                const resp = await getCandidateTimeline(id);
                if (resp && resp.success) {
                    const raw = resp.data;
                    // Extract events from various possible shapes
                    const maybeArray = Array.isArray(raw) ? raw : (raw?.timeline || raw?.events || raw?.items || raw?.data || []);
                    if (Array.isArray(maybeArray)) apiEvents = maybeArray;
                } else if (resp && resp.error) {
                    setError(resp.error);
                }
            }

            // Prefer API events if any; else fallback to application-provided events; else synthetic
            const sourceApp = apiEvents.length > 0 ? { ...application, timeline: apiEvents } : application;
            const withEvents = createTimelineFromEvents(sourceApp);
            const timelineData = withEvents && withEvents.length > 0
                ? withEvents
                : createTimelineFromApplication(application);
            setTimeline(timelineData);
        } catch (e) {
            console.error('[EnhancedTimelineModal] Timeline fetch error:', e);
            setError(e?.message || 'Failed to load timeline');
            // Fallback to synthetic/local generation
            const withEvents = createTimelineFromEvents(application);
            const timelineData = withEvents && withEvents.length > 0
                ? withEvents
                : createTimelineFromApplication(application);
            setTimeline(timelineData);
        } finally {
            setLoading(false);
        }
    }, [application, resolveOpeningCandidateDetailId, createTimelineFromEvents, createTimelineFromApplication]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            fetchAndGenerateTimeline();
        } else {
            document.body.style.overflow = 'auto';
        }
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, fetchAndGenerateTimeline]);

    const formatDate = (date) => {
        if (!date) return '';
        // Flutter screenshot shows: Aug 26, 2025
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        });
    };

    // Removed getTimeAgo as timeline UI no longer uses relative time

    if (!isOpen || !application) return null;

    return (
        <div className="etm-overlay" onClick={onClose}>
            <div className="etm-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="etm-header">
                    <button className="etm-close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                    <div className="etm-header-content">
                        <h2 className="etm-title">Application Timeline</h2>
                        <div className="etm-location">
                            <i className="fas fa-briefcase"></i>
                            <span>{application.jobTitle || 'Job Title'} at {application.company || 'Company'}</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="etm-content">
                    {loading ? (
                        <div className="etm-loading">
                            <div className="etm-spinner"></div>
                            <p>Loading timeline...</p>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="etm-error" style={{
                                    background: '#FEF2F2',
                                    color: '#B91C1C',
                                    border: '1px solid #FCA5A5',
                                    borderRadius: '8px',
                                    padding: '12px 14px',
                                    marginBottom: '12px'
                                }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                                    {typeof error === 'string' ? error : 'Unable to load latest timeline. Showing available information.'}
                                </div>
                            )}
                            {/* Timeline */}
                            <div className="etm-timeline-container">
                                <div className="etm-timeline" data-single-item={timeline.length === 1 ? "true" : "false"}>
                                    {timeline.map((rawItem, index) => {
                                        // Normalize to match Flutter look
                                        const item = { ...rawItem };
                                        if (item.status === 'Applied') {
                                            item.icon = 'fa-check-circle';
                                            item.color = '#10B981';
                                        }
                                        const dotColor = item.color || '#3B82F6';
                                        const iconClass = item.icon || 'fa-info-circle';

                                        return (
                                            <div key={index} className={`etm-timeline-item ${item.isActive ? 'active' : ''} ${item.isCompleted ? 'completed' : ''}`}>
                                                <div className="etm-timeline-dot" style={{ 
                                                    backgroundColor: dotColor,
                                                    borderColor: dotColor
                                                }}>
                                                    <i className={`fas ${iconClass}`} style={{ color: 'white' }}></i>
                                                </div>
                                                <div className="etm-timeline-content">
                                                    <div className="etm-timeline-row">
                                                        <div className="etm-timeline-left">
                                                            <h4 className="etm-timeline-status" style={{ color: item.color }}>
                                                                {item.status}
                                                            </h4>
                                                            {item.date && (
                                                                <p className="etm-timeline-date-inline" style={{ marginTop: '4px', color: '#6B7280' }}>
                                                                    {formatDate(item.date)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            
                        </>
                    )}
                </div>


            </div>
        </div>
    );
};

export default EnhancedTimelineModal;
